import { useEffect, useMemo, useState } from "react";

import { useDispatch } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";
import { Modal } from "@similarweb/ui-components";

import { showErrorToast, showSuccessToast } from "app/actions/toast_actions";
import { renderErrorToast } from "app/pages/sales-intelligence/common-components/ErrorToast/ErrorToast";
import { SuccessToast } from "app/pages/sales-intelligence/common-components/SuccessToast/SuccessToast";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";

import { useTeamsTrackingService } from "../../hooks/useTeamsTrackingService";
import { useCreateTeamMutation } from "../../queries/useCreateTeamMutation";
import { useTeamAccountUsersQuery } from "../../queries/useTeamAccountUsersQuery";
import { useTeamsListQuery } from "../../queries/useTeamsListQuery";
import { useUpdateTeamMembersMutation } from "../../queries/useUpdateTeamMembersMutation";
import { useUpdateTeamMutation } from "../../queries/useUpdateTeamMutation";
import type { TeamListItem, TeamUser } from "../../types/teams.types";

import { TeamMembersStep } from "./TeamMembersStep";
import { TeamNameStep } from "./TeamNameStep";

type Step = "name" | "members";

type TeamModalContainerProps = {
    isOpen: boolean;
    team?: TeamListItem;
    onClose: () => void;
    initialStep?: Step;
    lockedToStep?: boolean;
};

const TOAST_DURATION_MS = 3000;

const TEAM_MODAL_STYLES = {
    content: {
        width: `440px`,
        padding: "0",
    },
};

const toDescriptionPayload = (description: string): string | null => {
    const trimmed = description.trim();
    return trimmed.length > 0 ? trimmed : null;
};

// True when the two id lists represent different sets (ignoring order).
const hasIdSetDiff = (a: number[], b: number[]): boolean => {
    if (a.length !== b.length) {
        return true;
    }
    const setA = new Set(a);
    return b.some((id) => !setA.has(id));
};

export const TeamModalContainer = ({
    isOpen,
    team,
    onClose,
    initialStep = "name",
    lockedToStep = false,
}: TeamModalContainerProps) => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const tracking = useTeamsTrackingService();
    const isEditMode = team !== undefined;

    const [step, setStep] = useState<Step>(initialStep);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [selectedMembers, setSelectedMembers] = useState<TeamUser[]>([]);

    const {
        data: users = [],
        isLoading: isUsersLoading,
        isError: isUsersError,
    } = useTeamAccountUsersQuery({ enabled: isOpen && step === "members" });

    const { data: allTeams = [] } = useTeamsListQuery({ enabled: isOpen });
    const existingTeamNames = useMemo(
        () =>
            allTeams
                .filter((t) => t.teamId !== team?.teamId)
                .map((t) => t.name?.trim() ?? "")
                .filter((n) => n.length > 0),
        [allTeams, team?.teamId],
    );

    const { mutateAsync: createTeam, isPending: isCreating } = useCreateTeamMutation();
    const { mutateAsync: updateTeam, isPending: isUpdating } = useUpdateTeamMutation();
    const { mutateAsync: updateTeamMembers, isPending: isUpdatingMembers } = useUpdateTeamMembersMutation();
    const isSubmitting = isCreating || isUpdating || isUpdatingMembers;

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setStep(initialStep);
        setName(team?.name ?? "");
        setDescription(team?.description ?? "");
        setSelectedMembers(team ? [...team.users] : []);
    }, [isOpen, team?.teamId, initialStep]);

    const trimmedName = name.trim();
    const descriptionPayload = toDescriptionPayload(description);

    const handleDescriptionChange = (value: string | undefined) => {
        setDescription(value ?? "");
    };
    const handleGoToMembers = () => setStep("members");
    const handleGoToName = () => setStep("name");

    const handleRequestClose = () => {
        if (isSubmitting) {
            return;
        }
        onClose();
    };

    const showSuccess = (translationKey: string) => {
        dispatch(showSuccessToast(<SuccessToast content={translate(translationKey)} />, Date.now(), TOAST_DURATION_MS));
    };

    const showError = () => {
        dispatch(
            showErrorToast(
                renderErrorToast({
                    content: translate("si.common.toast.error"),
                    minWidth: "auto",
                }),
            ),
        );
    };

    const handleSaveNameOnly = async () => {
        if (!isEditMode) {
            return;
        }
        try {
            await updateTeam({
                teamId: team.teamId,
                name: trimmedName,
                description: descriptionPayload,
            });
            showSuccess("si.management.teams.edit.success.toast");
            onClose();
        } catch {
            showError();
            // Keep modal open so the user can retry.
        }
    };

    const handleSaveName = () => {
        void handleSaveNameOnly();
    };

    const handleSubmit = async (selectedUserIds: number[]) => {
        try {
            if (team !== undefined) {
                const hasNameChange = trimmedName !== (team.name ?? "");
                const hasDescriptionChange = descriptionPayload !== (team.description ?? null);
                const existingIds = team.users.map((m) => m.userId);
                const hasMembersChange = hasIdSetDiff(existingIds, selectedUserIds);

                if (hasNameChange || hasDescriptionChange) {
                    await updateTeam({
                        teamId: team.teamId,
                        name: trimmedName,
                        description: descriptionPayload,
                    });
                }
                if (hasMembersChange) {
                    await updateTeamMembers({ teamId: team.teamId, userIds: selectedUserIds });
                    const existingIdSet = new Set(existingIds);
                    const addedCount = selectedUserIds.filter((id) => !existingIdSet.has(id)).length;
                    if (addedCount > 0) {
                        tracking.trackMembersAdded(trimmedName, addedCount);
                    }
                }
                showSuccess("si.management.teams.edit.success.toast");
                onClose();
                return;
            }
            const created = await createTeam({
                name: trimmedName,
                description: descriptionPayload,
                userIds: selectedUserIds,
            });
            tracking.trackTeamCreated(trimmedName, selectedUserIds.length);
            showSuccess("si.management.teams.create.success.toast");
            onClose();
            void siNavigator.goToTeamDetailPage({ id: created.teamId, tab: "filters", isNewTeam: true });
        } catch {
            showError();
            // Keep modal open so the user can retry.
        }
    };

    const handleMembersSubmit = () => {
        void handleSubmit(selectedMembers.map((m) => m.userId));
    };

    const handleMembersCancel = () => {
        // Create flow: cancel on the members step still creates the team (without members)
        // in the background. Close the modal immediately and let the request finish silently.
        if (!isEditMode) {
            onClose();
            void createTeam({
                name: trimmedName,
                description: descriptionPayload,
                userIds: [],
            })
                .then((created) => {
                    tracking.trackTeamCreated(trimmedName, 0);
                    showSuccess("si.management.teams.create.success.toast");
                    void siNavigator.goToTeamDetailPage({ id: created.teamId, tab: "filters", isNewTeam: true });
                })
                .catch(() => {
                    showError();
                });
            return;
        }
        handleRequestClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={handleRequestClose}
            customStyles={TEAM_MODAL_STYLES}
            data-automation="team-modal"
        >
            {step === "name" ? (
                <TeamNameStep
                    submitLabelKey={
                        lockedToStep || isEditMode
                            ? "si.common.button.save"
                            : "si.management.teams.create.name.button.create"
                    }
                    name={name}
                    description={description}
                    existingNames={existingTeamNames}
                    onNameChange={setName}
                    onDescriptionChange={handleDescriptionChange}
                    onCancel={handleRequestClose}
                    onNext={lockedToStep ? handleSaveName : handleGoToMembers}
                    isFinalStep={lockedToStep}
                    isSubmitting={isSubmitting}
                />
            ) : (
                <TeamMembersStep
                    teamName={trimmedName}
                    users={users}
                    isUsersLoading={isUsersLoading}
                    isUsersError={isUsersError}
                    selectedMembers={selectedMembers}
                    onSelectionChange={setSelectedMembers}
                    onBack={handleGoToName}
                    onCancel={handleMembersCancel}
                    onSubmit={handleMembersSubmit}
                    isSubmitting={isSubmitting}
                    submitLabelKey={
                        isEditMode ? "si.common.button.save" : "si.management.teams.detail.add_members.submit"
                    }
                    hideBack={lockedToStep}
                />
            )}
        </Modal>
    );
};
